import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import storeProvisioningService, { StoreProvisioning } from '../../services/storeProvisioningService';
import { getApiErrorMessage } from '../../utils/apiError';

interface TenantStoreSectionProps {
  tenantId: number;
  disabled?: boolean;
}

const TenantStoreSection: React.FC<TenantStoreSectionProps> = ({ tenantId, disabled = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingAdmin, setOpeningAdmin] = useState(false);
  const [data, setData] = useState<StoreProvisioning | null>(null);
  const [domain, setDomain] = useState('');
  const [storeEnabled, setStoreEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const provisioning = await storeProvisioningService.getByTenant(tenantId);
      setData(provisioning);
      setDomain(provisioning.domain ?? '');
      setStoreEnabled(provisioning.storeEnabled);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar configuración de tienda'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await storeProvisioningService.updateByTenant(tenantId, {
        domain: domain.trim() || undefined,
        storeEnabled,
      });
      setData(updated);
      enqueueSnackbar('Configuración de tienda guardada', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAdmin = async () => {
    if (!data?.adminUrl) {
      enqueueSnackbar('Configure dominio y active la tienda', { variant: 'warning' });
      return;
    }
    try {
      setOpeningAdmin(true);
      const handoff = await storeProvisioningService.createAdminHandoff();
      const callbackUrl = new URL(`${data.adminUrl.replace(/\/admin\/?$/, '')}/admin/auth/callback`);
      callbackUrl.searchParams.set('token', handoff.accessToken);
      window.open(callbackUrl.toString(), '_blank', 'noopener,noreferrer');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo abrir administración'), { variant: 'error' });
    } finally {
      setOpeningAdmin(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} maxWidth={720}>
      <Typography variant="body2" color="text.secondary">
        Provisión de tienda para este tenant. La apariencia y el catálogo se configuran en el panel /admin del Store.
      </Typography>

      <Alert severity={storeEnabled ? 'success' : 'info'}>
        {storeEnabled ? 'Tienda habilitada' : 'Tienda deshabilitada'}
        {data?.suggestedSubdomain ? ` · Sugerido: ${data.suggestedSubdomain}` : ''}
      </Alert>

      <FormControlLabel
        control={
          <Switch
            checked={storeEnabled}
            onChange={(e) => setStoreEnabled(e.target.checked)}
            disabled={disabled || saving}
          />
        }
        label="Tienda habilitada"
      />

      <TextField
        label="Dominio de tienda"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        fullWidth
        disabled={disabled || saving}
        helperText="Dominio público (custom o subdominio)"
        inputProps={{ maxLength: 255 }}
      />

      {data?.storeUrl && (
        <Typography variant="body2" color="text.secondary">
          URL:{' '}
          <Link href={data.storeUrl} target="_blank" rel="noopener noreferrer">
            {data.storeUrl}
          </Link>
        </Typography>
      )}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={handleSave} disabled={disabled || saving}>
          {saving ? 'Guardando...' : 'Guardar tienda'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenAdmin}
          disabled={disabled || openingAdmin || !storeEnabled}
        >
          Abrir administración
        </Button>
      </Stack>
    </Stack>
  );
};

export default TenantStoreSection;
